import { IconButton, IconButtonWithText } from '..';
import { useTranslation } from '../../../hooks/useTranslation';
import { useState, useCallback, useEffect, useRef } from 'react';
import { TermDto, PostTermsDto } from '../../../commonTypes/client.types';
import useHttpPostPaymentTerms from '../../../hooks/client/clientApi/useHttpPostPaymentTerms';
import useHttpPatchPaymentTerm from '../../../hooks/client/clientApi/useHttpPatchPaymentTerm';
import useHttpDeletePaymentTerm from '../../../hooks/client/clientApi/useHttpDeletePaymentTerm';
import { ToastStateHandler } from '../../../api';
import { AppError, getUserMessage } from '../../../api/utils/ErrorHandler';
import { useContainer } from '../../../hooks/useContainer';

interface TermModalProps {
    onSave: (saveCallback: () => Promise<void>) => void;
    onDirtyChange?: (isDirty: boolean) => void;
    terms: Partial<TermDto>[];
    refetchTerms: () => void;
}

const isProtectedTerm = (term?: Partial<TermDto>) => Boolean(term?.isPredefined || term?.organizationId === null);

const getComparableTerms = (terms: Partial<TermDto>[]) =>
    terms.map(term => ({
        id: term.id ?? null,
        name: term.name ?? '',
        numberOfDays: term.numberOfDays ?? 0,
    }));

const getTermComparableById = (terms: Partial<TermDto>[]) =>
    new Map(
        getComparableTerms(terms)
            .filter(term => term.id)
            .map(term => [term.id as string, term])
    );

const hasTermListChanged = (currentTerms: Partial<TermDto>[], initialTerms: Partial<TermDto>[]) => {
    const comparableCurrent = getComparableTerms(currentTerms);
    const comparableInitial = getComparableTerms(initialTerms);

    if (comparableCurrent.length !== comparableInitial.length) return true;

    const currentById = new Map(comparableCurrent.filter(term => term.id).map(term => [term.id as string, term]));
    const initialById = new Map(comparableInitial.filter(term => term.id).map(term => [term.id as string, term]));

    if (currentById.size !== initialById.size) return true;

    for (const [id, currentTerm] of currentById.entries()) {
        const initialTerm = initialById.get(id);
        if (!initialTerm) return true;
        if (currentTerm.name !== initialTerm.name || currentTerm.numberOfDays !== initialTerm.numberOfDays) return true;
    }

    const currentNewTerms = comparableCurrent.filter(term => !term.id);
    const initialNewTerms = comparableInitial.filter(term => !term.id);

    if (currentNewTerms.length !== initialNewTerms.length) return true;

    for (let index = 0; index < currentNewTerms.length; index++) {
        const currentTerm = currentNewTerms[index];
        const initialTerm = initialNewTerms[index];
        if (!initialTerm) return true;
        if (currentTerm.name !== initialTerm.name || currentTerm.numberOfDays !== initialTerm.numberOfDays) return true;
    }

    return false;
};

export const InvoiceTermModal = ({ onSave, onDirtyChange, terms: initialTerms, refetchTerms }: TermModalProps) => {
    const { t } = useTranslation('invoice');
    const { t: tNotifications } = useTranslation('newNotifications');
    const { execPost } = useHttpPostPaymentTerms();
    const { execPatch } = useHttpPatchPaymentTerm();
    const { execDelete } = useHttpDeletePaymentTerm();
    const toastHandler = useContainer(ToastStateHandler);

    const [terms, setTerms] = useState<Partial<TermDto>[]>(() => initialTerms.map(term => ({ ...term })));
    const latestTermsRef = useRef<Partial<TermDto>[]>(initialTerms.map(term => ({ ...term })));
    const initialTermsByIdRef = useRef<Map<string, { id: string | null; name: string; numberOfDays: number }>>(getTermComparableById(initialTerms));
    const deletedTermIdsRef = useRef<string[]>([]);

    useEffect(() => {
        const clonedTerms = initialTerms.map(term => ({ ...term }));
        setTerms(clonedTerms);
        latestTermsRef.current = clonedTerms;
        initialTermsByIdRef.current = getTermComparableById(initialTerms);
        deletedTermIdsRef.current = [];
    }, [initialTerms]);

    const handleAddNew = () => {
        setTerms([...terms, { name: '', numberOfDays: 0 }]);
    };

    const handleChange = (index: number, field: keyof TermDto, value: string | number) => {
        const term = terms[index];
        if (isProtectedTerm(term)) return;

        const updated = [...terms];
        if (field === 'numberOfDays') {
            updated[index] = { ...updated[index], numberOfDays: Number(value) };
        } else if (field === 'name') {
            updated[index] = { ...updated[index], name: String(value) };
        }
        setTerms(updated);
    };

    const handleDelete = (index: number) => {
        const term = terms[index];
        if (!term) return;
        if (isProtectedTerm(term)) return;

        if (!term.id) {
            const updated = terms.filter((_, i) => i !== index);
            setTerms(updated);
            return;
        }

        deletedTermIdsRef.current = [...deletedTermIdsRef.current, term.id];
        setTerms(prevTerms => prevTerms.filter(prevTerm => prevTerm.id !== term.id));
    };

    const patchTermAsync = useCallback(
        (id: string, body: PostTermsDto) =>
            new Promise<void>((resolve, reject) => {
                execPatch(id, body, {
                    onSuccess: resolve,
                    onError: reject,
                });
            }),
        [execPatch]
    );

    const postTermsAsync = useCallback(
        (newTerms: PostTermsDto[]) =>
            new Promise<void>((resolve, reject) => {
                execPost(newTerms, {
                    onSuccess: resolve,
                    onError: reject,
                });
            }),
        [execPost]
    );

    const deleteTermAsync = useCallback(
        (id: string) =>
            new Promise<void>((resolve, reject) => {
                execDelete(id, {
                    onSuccess: resolve,
                    onError: reject,
                });
            }),
        [execDelete]
    );

    useEffect(() => {
        latestTermsRef.current = terms;
    }, [terms]);

    useEffect(() => {
        const hasChanges = hasTermListChanged(terms, initialTerms) || deletedTermIdsRef.current.length > 0;
        onDirtyChange?.(hasChanges);
    }, [terms, initialTerms, onDirtyChange]);

    const handleSave = useCallback(async () => {
        const currentTerms = latestTermsRef.current;
        const deletedTermIds = deletedTermIdsRef.current;
        const newTerms: PostTermsDto[] = currentTerms
            .filter(term => !term.id && term.name && term.numberOfDays !== undefined)
            .map(term => ({
                name: term.name!,
                numberOfDays: term.numberOfDays!,
            }));

        const termsForUpdate = currentTerms
            .filter(term => term.id && !isProtectedTerm(term) && term.name && term.numberOfDays !== undefined)
            .map(term => {
                const comparableCurrent = {
                    name: term.name ?? '',
                    numberOfDays: term.numberOfDays ?? 0,
                };
                const comparableInitial = initialTermsByIdRef.current.get(term.id!);

                if (
                    !comparableInitial ||
                    (comparableInitial.name === comparableCurrent.name && comparableInitial.numberOfDays === comparableCurrent.numberOfDays)
                ) {
                    return null;
                }

                return {
                    id: term.id!,
                    body: {
                        name: term.name!,
                        numberOfDays: term.numberOfDays!,
                    },
                };
            })
            .filter((term): term is { id: string; body: PostTermsDto } => Boolean(term));

        if (newTerms.length === 0 && termsForUpdate.length === 0 && deletedTermIds.length === 0) {
            refetchTerms();
            return;
        }

        try {
            await Promise.all([
                ...deletedTermIds.map(id => deleteTermAsync(id)),
                ...termsForUpdate.map(term => patchTermAsync(term.id, term.body)),
                ...(newTerms.length > 0 ? [postTermsAsync(newTerms)] : []),
            ]);

            deletedTermIdsRef.current = [];
            onDirtyChange?.(false);
            toastHandler.push('success', tNotifications('invoice.termsUpdateTitle'), tNotifications('invoice.termsUpdateSuccess'));
            refetchTerms();
        } catch (error) {
            const message = getUserMessage(error as AppError | string, tNotifications('invoice.termsUpdateError'));
            toastHandler.push('error', tNotifications('invoice.termsUpdateTitle'), message);
            throw error;
        }
    }, [deleteTermAsync, onDirtyChange, patchTermAsync, postTermsAsync, refetchTerms, tNotifications, toastHandler]);

    useEffect(() => {
        onSave(handleSave);
    }, [onSave, handleSave]);

    return (
        <div className="invoice-term">
            <div className="invoice-term__content-container">
                <div className="invoice-create__table-container">
                    <table className="invoice-create__table">
                        <thead>
                            <tr className="invoice-create__table-row">
                                <th className="invoice-create__table-head-cell">
                                    <div className="invoice-create__header-cell">
                                        <span className="invoice-create__bold-text">{t('termName')}</span>
                                    </div>
                                </th>
                                <th className="invoice-create__table-head-cell">
                                    <div className="invoice-create__header-cell">
                                        <span className="invoice-create__bold-text">{t('termDay')}</span>
                                    </div>
                                </th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {terms.map((term, index) => (
                                <tr key={term.id || index} className="invoice-create__table-row">
                                    <td className="invoice-create__table-cell">
                                        <input
                                            type="text"
                                            value={term.name ?? ''}
                                            placeholder="Enter Term Name"
                                            onChange={e => handleChange(index, 'name', e.target.value)}
                                            className="invoice-create__currency-select paragraph-3"
                                            disabled={isProtectedTerm(term)}
                                        />
                                    </td>
                                    <td className="invoice-create__table-cell">
                                        <input
                                            type="number"
                                            value={term.numberOfDays ?? 0}
                                            placeholder="0"
                                            onChange={e => handleChange(index, 'numberOfDays', e.target.value)}
                                            className="invoice-create__currency-select paragraph-3"
                                            disabled={isProtectedTerm(term)}
                                        />
                                    </td>
                                    <td className="invoice-create__table-cell-actions">
                                        {!isProtectedTerm(term) && (
                                            <div className="invoice-create__table-cell-action">
                                                <IconButton
                                                    type="button"
                                                    iconUrl="/svg/close-x-red.svg"
                                                    variant="icon"
                                                    iconAlt="delete"
                                                    onClick={() => handleDelete(index)}
                                                />
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div>
                    <IconButtonWithText
                        onClick={handleAddNew}
                        iconSrc="/svg/plus-blue-without-circle.svg"
                        iconAlt="add"
                        variant="text"
                        textColour="blue"
                        text={t('addNewTerm')}
                    />
                </div>
            </div>
        </div>
    );
};
